// safe-return-to.ts — Plan 08-11 Task 2 (W5).
//
// Open-redirect defense for the ?returnTo= query parameter.
//
// SHARED SOURCE OF TRUTH (W5)
// ---------------------------
// Imported by:
//   - apps/web/src/pages/auth/SignIn.tsx          (this plan, Task 2)
//   - apps/web/src/pages/author/RecipeEditor.tsx  (Plan 08-10's 401 fallback)
//
// DO NOT inline the regex at either call site. The whole point of pulling the
// allow-list into one module is so a future change to the safe destinations
// (e.g., adding "/dashboard/") happens here once and both call sites benefit
// automatically. The acceptance test asserts SignIn.tsx contains no inline
// regex literal — same discipline applies to RecipeEditor.
//
// ALLOW-LIST DESIGN (per 08-RESEARCH Open Question 2)
// ----------------------------------------------------
// Regex: ^/(?:author/|account/|$)
//
//   Accepts:
//     "/"                          → root, exact match via the $ alternative
//     "/author/recipe/abc"         → author/* matches via the "author/" prefix
//     "/account/redeem-invite"     → account/* matches via the "account/" prefix
//
//   Refuses (all fall back to "/"):
//     "/javascript:foo"            → no /author/ or /account/ prefix
//     "//attacker.example/path"    → protocol-relative URL; second char is "/"
//                                    not "a", and not end-of-string
//     "/authoritah-attacker"       → prefix-substring attack defeated by the
//                                    REQUIRED trailing slash on "author/"
//     "/account"                   → no trailing slash on the bare prefix —
//                                    refused for consistency with the regex
//     "https://attacker.example"   → does not start with "/"
//     "javascript:alert(1)"        → no leading "/" at all
//     ""                           → empty string does not start with "/"
//     null                         → coerced to "/" by the null-guard branch
//
// The regex is anchored at "^/" — every accepted value begins with a slash.
// After that slash, exactly one of three things must follow:
//   1. "author/"   → matches a relative path under /author/...
//   2. "account/"  → matches a relative path under /account/...
//   3. end-of-string → exactly the root "/"
//
// THREAT MITIGATION (08-11 threat register T-08-11-06):
//   The original lean regex `^/(?:author|account|$|/)` is unsafe — the trailing
//   `|/` alternative collapses to "any path beginning with /", which accepts
//   `/javascript:foo`, `/authoritah-attacker`, etc. The corrected regex above
//   requires trailing slashes on the prefixes and uses `$` (not `|/`) for the
//   root-match branch.

/**
 * Structural path-safety primitive (WR-04 — single source of truth).
 *
 * Returns true only for a same-origin RELATIVE path that is structurally safe
 * to navigate to: a single leading "/", NOT protocol-relative ("//host"), no
 * backslash tricks, no embedded scheme ("/javascript:..."), and no ".."
 * traversal segments. It deliberately does NOT impose an allow-list of route
 * prefixes — that is the caller's concern.
 *
 * This is the shared primitive both redirect validators call:
 *   - computeAllowedReturnTo() layers the ?returnTo= allow-list on top of it.
 *   - ExtractForm's server-supplied redirectTo check uses it directly, because
 *     its safe destination (/recipes/<id>/review) is outside the returnTo
 *     allow-list but must still be structurally safe.
 *
 * Centralizing the structural checks here removes the drift WR-04 flagged: a
 * future hardening of the path-safety rules now protects every call site at
 * once. DO NOT re-inline these checks at a call site.
 */
export function isSafeInternalPath(path: string): boolean {
  if (path.length === 0) return false;
  if (path[0] !== "/") return false; // must be a relative path
  if (path.startsWith("//")) return false; // protocol-relative → off-origin
  if (path.includes("\\")) return false; // backslash tricks
  if (/^\/+[a-z][a-z0-9+.-]*:/i.test(path)) return false; // embedded scheme
  if (path.split("/").some((seg) => seg === "..")) return false; // traversal
  return true;
}

/**
 * Map an untrusted `returnTo` query param to a SAFE internal path.
 *
 * Returns "/" for null, empty, malformed, or off-allow-list values. Returns
 * the input unchanged for values that match the allow-list.
 *
 * @param rawReturnTo — the value read from URLSearchParams.get("returnTo");
 *                      may be null when the query parameter is absent.
 */
export function computeAllowedReturnTo(rawReturnTo: string | null): string {
  if (rawReturnTo === null) return "/";
  // Structural safety first (shared primitive): rejects protocol-relative,
  // embedded-scheme, backslash, and ".." (WR-01 traversal) payloads. Folding
  // these into isSafeInternalPath means computeAllowedReturnTo and ExtractForm
  // can never drift on the structural rules.
  if (!isSafeInternalPath(rawReturnTo)) return "/";
  // Then the returnTo allow-list: only the root or the /author/ and /account/
  // subtrees are valid post-sign-in destinations.
  if (!/^\/(?:author\/|account\/|$)/.test(rawReturnTo)) return "/";
  return rawReturnTo;
}
